import React, { useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, money, formatApiError, hexToHsl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, XCircle, CalendarClock, Store, Ban, Phone, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { DICT, DF_LOCALE, DATE_LOCALE, LangFlags, getStoredLang, setStoredLang } from "@/lib/bookingI18n";

const fmtDate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookingManage() {
  const { token } = useParams();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get("lang");
  const [lang, setLangState] = useState(() => {
    if (urlLang === "tr" || urlLang === "en" || urlLang === "de") { setStoredLang(urlLang); return urlLang; }
    return getStoredLang();
  });
  const setLang = (l) => { setLangState(l); setStoredLang(l); };
  const L = DICT[lang];
  const loc = DATE_LOCALE[lang];
  const [mode, setMode] = useState(null); // null | "reschedule"
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["manage-res", token],
    queryFn: () => api.get(`/public/reservations/${token}`).then((r) => r.data),
    retry: false,
  });

  const dateStr = date ? fmtDate(date) : null;
  const { data: avail, isFetching: loadingSlots } = useQuery({
    queryKey: ["manage-avail", token, dateStr],
    queryFn: () => api.get(`/public/reservations/${token}/availability`, { params: { date: dateStr } }).then((r) => r.data),
    enabled: mode === "reschedule" && !!dateStr,
  });
  const today = useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);

  const disabledDays = useMemo(() => {
    const wh = data?.business?.working_hours;
    const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return (d) => {
      if (d < today) return true;
      if (!wh) return false;
      const day = wh[dayKeys[d.getDay()]];
      return !day || day.closed;
    };
  }, [data, today]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/public/reservations/${token}/cancel`);
      qc.invalidateQueries({ queryKey: ["manage-res", token] });
      toast.success(L.cancelToast);
      setMode(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reschedule = async () => {
    if (!dateStr || !time) return;
    setBusy(true);
    try {
      await api.post(`/public/reservations/${token}/reschedule`, { date: dateStr, time });
      qc.invalidateQueries({ queryKey: ["manage-res", token] });
      toast.success(L.rescheduleToast);
      setMode(null); setDate(null); setTime(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const STATUS = {
    pending: { label: L.statusPending, cls: "bg-amber-100 text-amber-700" },
    confirmed: { label: L.statusConfirmed, cls: "bg-emerald-100 text-emerald-700" },
    completed: { label: L.statusCompleted, cls: "bg-blue-100 text-blue-700" },
    cancelled: { label: L.statusCancelled, cls: "bg-red-100 text-red-700" },
  };

  const header = (
    <div className="w-full max-w-md flex items-center justify-between mb-6">
      <Link to="/" className="flex items-center gap-2" data-testid="manage-brand-link" aria-label="365Rez">
        <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
          <CalendarClock className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-display font-bold text-2xl">365Rez</span>
      </Link>
      <LangFlags lang={lang} onChange={setLang} />
    </div>
  );

  if (isLoading)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (isError || !data)
    return (
      <div className="min-h-screen grain-bg flex flex-col items-center justify-center p-5" translate="no">
        {header}
        <div className="text-center">
          <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold">{L.manageNotFound}</h1>
          <p className="text-muted-foreground mt-2">{L.manageNotFoundDesc}</p>
        </div>
      </div>
    );

  const r = data.reservation;
  const b = data.business;
  const themeHsl = hexToHsl(b.theme_color);
  const themeStyle = themeHsl ? { "--primary": themeHsl, "--ring": themeHsl } : {};
  const st = STATUS[r.status] || STATUS.pending;
  const cancelled = r.status === "cancelled";

  return (
    <div className="min-h-screen grain-bg flex flex-col items-center justify-center p-5" style={themeStyle} translate="no">
      {header}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8" data-testid="manage-page">
        <div className="text-center">
          <h1 className="text-2xl font-bold">{L.manageTitle}</h1>
          <p className="text-muted-foreground mt-1">{b.name}</p>
          <span className={cn("inline-block mt-3 text-xs font-semibold px-3 py-1 rounded-full", st.cls)} data-testid="manage-status">{st.label}</span>
        </div>

        <div className="mt-6 rounded-xl bg-secondary p-5 text-left space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">{L.nameRow}</span><span className="font-medium">{r.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{L.serviceLabel}</span><span className="font-medium">{r.service_name}</span></div>
          {r.staff_name && <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><User className="w-3.5 h-3.5" />{L.staffLabel}</span><span className="font-medium">{r.staff_name}</span></div>}
          <div className="flex justify-between"><span className="text-muted-foreground">{L.dateLabel}</span><span className="font-medium">{new Date(r.date + "T00:00:00").toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric", weekday: "long" })}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{L.timeLabel}</span><span className="font-medium">{r.time}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{L.priceLabel}</span><span className="font-bold text-primary">{money(r.service_price)}</span></div>
        </div>

        {cancelled ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700 flex items-center justify-center gap-2" data-testid="cancelled-note">
            <XCircle className="w-5 h-5" /> {L.cancelledNote}
          </div>
        ) : !data.can_modify ? (
          <p className="mt-6 text-center text-sm text-muted-foreground">{L.pastNote}</p>
        ) : mode === "reschedule" ? (
          <div className="mt-6" data-testid="reschedule-panel">
            <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-primary" /> {L.pickNewDate}</p>
            <div className="rounded-xl border border-border bg-card p-2 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  setDate(d);
                  setTime(null);
                }}
                disabled={disabledDays}
                hidden={{ before: today }}
                fromMonth={today}
                showOutsideDays={false}
                weekStartsOn={1}
                locale={DF_LOCALE[lang]}
              />
            </div>
            {date && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2 capitalize">{date.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long" })} {L.pickTimeSuffix}</p>
                {loadingSlots ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : (avail?.slots?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="reschedule-no-slots">{L.noSlotsManage}</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {avail.slots.map((t) => (
                      <button key={t} onClick={() => setTime(t)}
                        className={cn("h-11 rounded-lg border font-semibold text-sm transition-all active:scale-95",
                          time === t ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40")}
                        data-testid="reschedule-slot">{t}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <Button variant="outline" className="rounded-full h-12 flex-1" onClick={() => { setMode(null); setDate(null); setTime(null); }} data-testid="reschedule-cancel-btn">{L.giveUp}</Button>
              <Button className="rounded-full h-12 flex-1" disabled={!time || busy} onClick={reschedule} data-testid="reschedule-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L.doReschedule}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <Button className="w-full rounded-full h-12" onClick={() => setMode("reschedule")} data-testid="start-reschedule-btn">
              <CalendarClock className="w-4 h-4 mr-2" /> {L.startReschedule}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full rounded-full h-12 text-destructive border-destructive/30 hover:bg-red-50" disabled={busy} data-testid="cancel-booking-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-2" /> {L.cancelBooking}</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cancel-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{L.cancelTitle}</AlertDialogTitle>
                  <AlertDialogDescription>{L.cancelDesc}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-dialog-dismiss">{L.giveUp}</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} className="bg-destructive hover:bg-destructive/90" data-testid="cancel-dialog-confirm">{L.cancelYes}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {b.phone && (
          <a href={`tel:${b.phone}`} className="flex items-center justify-center gap-1.5 mt-5 text-sm text-muted-foreground hover:text-primary transition-colors">
            <Phone className="w-3.5 h-3.5" /> {b.phone}
          </a>
        )}
      </motion.div>
      <Link to="/kayit" data-testid="powered-by-link"
        className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
        <CalendarClock className="w-3.5 h-3.5" /> {L.poweredBy}
      </Link>
    </div>
  );
}
